import { useMemo } from 'react';

interface OptimizedImageOptions {
  width?: number;
  quality?: number;
  format?: 'webp' | 'avif' | 'jpeg' | 'png';
}

// Low quality placeholder dimensions
const PLACEHOLDER_WIDTH = 20;
const PLACEHOLDER_QUALITY = 10;

/**
 * Hook to generate optimized image URLs with responsive srcset
 * Supports Supabase Storage and Unsplash image transformations
 */
export function useOptimizedImage(
  imageUrl: string | null | undefined,
  options: OptimizedImageOptions = {}
) {
  const { width = 400, quality = 75, format = 'webp' } = options;

  return useMemo(() => {
    if (!imageUrl) {
      return {
        src: '/og-image.png',
        srcSet: undefined,
        blurDataUrl: undefined,
        isOptimized: false,
      };
    }

    // Check if it's a Supabase storage URL
    const isSupabaseStorage = imageUrl.includes('supabase.co/storage');
    
    // Check if it's an Unsplash URL
    const isUnsplash = imageUrl.includes('unsplash.com');
    
    if (isSupabaseStorage) {
      const baseUrl = imageUrl.split('?')[0];
      
      // Generate responsive srcset with optimized widths
      const widths = [320, 480, 640, 768, 1024, 1280, 1536, 1920];
      const srcSet = widths
        .filter(w => w <= Math.min(width * 2, 1920))
        .map(w => {
          const transformUrl = `${baseUrl}?width=${w}&quality=${quality}&format=${format}`;
          return `${transformUrl} ${w}w`;
        })
        .join(', ');

      // Generate tiny blur placeholder
      const blurDataUrl = `${baseUrl}?width=${PLACEHOLDER_WIDTH}&quality=${PLACEHOLDER_QUALITY}&format=webp`;

      return {
        src: `${baseUrl}?width=${width}&quality=${quality}&format=${format}`,
        srcSet: srcSet || undefined,
        blurDataUrl,
        isOptimized: true,
      };
    }

    if (isUnsplash) {
      // Parse existing Unsplash URL and extract base
      const url = new URL(imageUrl);
      const basePath = url.origin + url.pathname;
      
      // Unsplash-specific optimizations
      const unsplashParams = new URLSearchParams({
        w: width.toString(),
        q: quality.toString(),
        fm: format === 'avif' ? 'webp' : format, // Unsplash doesn't support avif, use webp
        fit: 'crop',
        auto: 'format,compress',
        cs: 'srgb',
      });

      // Generate responsive srcset
      const widths = [320, 480, 640, 768, 1024, 1280, 1536, 1920];
      const srcSet = widths
        .filter(w => w <= Math.min(width * 2, 1920))
        .map(w => {
          const params = new URLSearchParams({
            w: w.toString(),
            q: quality.toString(),
            fm: 'webp',
            fit: 'crop',
            auto: 'format,compress',
          });
          return `${basePath}?${params.toString()} ${w}w`;
        })
        .join(', ');

      // Blur placeholder with tiny dimensions
      const blurParams = new URLSearchParams({
        w: PLACEHOLDER_WIDTH.toString(),
        q: PLACEHOLDER_QUALITY.toString(),
        fm: 'webp',
        fit: 'crop',
        blur: '20',
      });

      return {
        src: `${basePath}?${unsplashParams.toString()}`,
        srcSet,
        blurDataUrl: `${basePath}?${blurParams.toString()}`,
        isOptimized: true,
      };
    }

    // For other external URLs, return as-is
    return {
      src: imageUrl,
      srcSet: undefined,
      blurDataUrl: undefined,
      isOptimized: false,
    };
  }, [imageUrl, width, quality, format]);
}

/**
 * Generate sizes attribute for responsive images
 */
export function generateSizes(breakpoints: Record<string, string>): string {
  return Object.entries(breakpoints)
    .map(([breakpoint, size]) => {
      if (breakpoint === 'default') return size;
      return `(max-width: ${breakpoint}) ${size}`;
    })
    .join(', ');
}

/**
 * Default sizes for common use cases
 */
export const IMAGE_SIZES = {
  card: '(max-width: 480px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 400px',
  hero: '100vw',
  thumbnail: '(max-width: 480px) 50vw, (max-width: 768px) 33vw, 200px',
  avatar: '48px',
  listing: '(max-width: 480px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 400px',
  gallery: '(max-width: 480px) 100vw, (max-width: 768px) 50vw, 600px',
};

/**
 * Preload critical images for LCP optimization
 */
export function preloadImage(src: string, as: 'image' = 'image'): void {
  if (typeof document === 'undefined') return;
  
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = as;
  link.href = src;
  link.type = 'image/webp';
  document.head.appendChild(link);
}
